import { SECONDARY_LEVEL_NAV_OPTIONS, TOP_LEVEL_NAV_OPTIONS } from '@/constants/nav';
import { getDefaultDrawerPrompts } from '@/ask-pendo/utils/ask-pendo-quick-start';

/**
 * Starter questions for pages Leo has no per-entity context for, keyed by the nav area the
 * route belongs to. Routes already declare `meta.activeNavOption` to drive left-nav highlighting,
 * so an area entry covers every route beneath it — including ones added later — without any
 * per-route bookkeeping.
 *
 * Questions must be answerable with nothing but the area the user is looking at, since that is
 * all Leo gets. Pages that publish real context to the leo-page-context store keep their own,
 * sharper questions; these only fill the gap.
 */
const STARTER_QUESTIONS_BY_SECONDARY_NAV = {
    [SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS]: ($t) => [
        $t('askPendo.starterQuestions.What does Pendo define as an account?'),
        $t('askPendo.starterQuestions.How are my accounts performing overall?'),
        $t('askPendo.starterQuestions.Which accounts have been least active recently?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.VISITORS]: ($t) => [
        $t('askPendo.starterQuestions.What does Pendo define as a visitor?'),
        $t('askPendo.starterQuestions.How many visitors were active in the last 30 days?'),
        $t('askPendo.starterQuestions.Which visitors are the most engaged with my product?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.SEGMENTS]: ($t) => [
        $t('askPendo.starterQuestions.What is a segment and when should I use one?'),
        $t('askPendo.starterQuestions.How do I build a segment of my most engaged accounts?'),
        $t('askPendo.starterQuestions.What is the difference between a segment and a filter?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.PAGES]: ($t) => [
        $t('askPendo.starterQuestions.What does Pendo count as a page view?'),
        $t('askPendo.starterQuestions.Which pages are getting the most traffic?'),
        $t('askPendo.starterQuestions.How do I tag a new page?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.FEATURES]: ($t) => [
        $t('askPendo.starterQuestions.What does Pendo count as a feature click?'),
        $t('askPendo.starterQuestions.Which features have the lowest adoption?'),
        $t('askPendo.starterQuestions.How do I tag a new feature?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.TRACK_EVENTS]: ($t) => [
        $t('askPendo.starterQuestions.What is a track event and when should I use one?'),
        $t('askPendo.starterQuestions.How do I send a track event to Pendo?'),
        $t('askPendo.starterQuestions.Which track events fire most often?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.PES]: ($t) => [
        $t('askPendo.starterQuestions.How is the Product Engagement Score calculated?'),
        $t('askPendo.starterQuestions.What counts as a healthy Product Engagement Score?'),
        $t('askPendo.starterQuestions.How has my Product Engagement Score changed recently?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.REPORTS]: ($t) => [
        $t('askPendo.starterQuestions.What can I build with a report in Pendo?'),
        $t('askPendo.starterQuestions.Which report should I use to compare usage over time?'),
        $t('askPendo.starterQuestions.How do I share a report with my team?'),
    ],
    [SECONDARY_LEVEL_NAV_OPTIONS.AI_AGENTS]: ($t) => [
        $t('askPendo.starterQuestions.What does Agent Analytics measure?'),
        $t('askPendo.starterQuestions.How are my AI agents performing?'),
        $t('askPendo.starterQuestions.Where are my agents failing most often?'),
    ],
};

const STARTER_QUESTIONS_BY_TOP_NAV = {
    [TOP_LEVEL_NAV_OPTIONS.PEOPLE]: ($t) => [
        $t('askPendo.starterQuestions.What is the difference between a visitor and an account?'),
        $t('askPendo.starterQuestions.Who are my most engaged customers right now?'),
        $t('askPendo.starterQuestions.How do I find customers who have gone quiet?'),
    ],
    [TOP_LEVEL_NAV_OPTIONS.PRODUCT]: ($t) => [
        $t('askPendo.starterQuestions.What should I be tracking in my product?'),
        $t('askPendo.starterQuestions.How do I tag pages and features?'),
        $t('askPendo.starterQuestions.Which parts of my product get the most use?'),
    ],
    [TOP_LEVEL_NAV_OPTIONS.DASHBOARDS]: ($t) => [
        $t('askPendo.starterQuestions.What should I put on a dashboard?'),
        $t('askPendo.starterQuestions.How do I build a dashboard from a template?'),
        $t('askPendo.starterQuestions.Which widgets are best for tracking adoption?'),
    ],
    [TOP_LEVEL_NAV_OPTIONS.GUIDES]: ($t) => [
        $t('askPendo.starterQuestions.What makes an in-app guide effective?'),
        $t('askPendo.starterQuestions.How are my guides performing?'),
        $t('askPendo.starterQuestions.How do I decide who should see a guide?'),
    ],
    [TOP_LEVEL_NAV_OPTIONS.REPLAY]: ($t) => [
        $t('askPendo.starterQuestions.What can session replay show me that analytics cannot?'),
        $t('askPendo.starterQuestions.How do I find replays of visitors who struggled?'),
        $t('askPendo.starterQuestions.How do I share a replay with my team?'),
    ],
    [TOP_LEVEL_NAV_OPTIONS.LISTEN]: ($t) => [
        $t('askPendo.starterQuestions.What are the top themes in my feedback right now?'),
        $t('askPendo.starterQuestions.How does Pendo group feedback into themes?'),
        $t('askPendo.starterQuestions.Which feedback should I prioritize next?'),
    ],
    [TOP_LEVEL_NAV_OPTIONS.SURVEYS]: ($t) => [
        $t('askPendo.starterQuestions.How do I run an NPS survey?'),
        $t('askPendo.starterQuestions.What is a good response rate for a survey?'),
        $t('askPendo.starterQuestions.What are my survey responses telling me?'),
    ],
    [TOP_LEVEL_NAV_OPTIONS.ROADMAPS]: ($t) => [
        $t('askPendo.starterQuestions.What is a roadmap in Pendo used for?'),
        $t('askPendo.starterQuestions.How do I keep my roadmap up to date?'),
        $t('askPendo.starterQuestions.How do I decide what to build next?'),
    ],
};

/**
 * Narrowest matching area wins, so a route can sit under a broad top-level entry until its own
 * area earns sharper questions. Falls back to the product-wide prompts when neither is mapped.
 */
export function getStarterQuestionsForRoute (route, $t) {
    const { secondaryOptionName, topOptionName } = route?.meta?.activeNavOption ?? {};
    const buildQuestions =
        STARTER_QUESTIONS_BY_SECONDARY_NAV[secondaryOptionName] ?? STARTER_QUESTIONS_BY_TOP_NAV[topOptionName];

    return buildQuestions ? buildQuestions($t) : getDefaultDrawerPrompts($t);
}
